import { useEffect, useState, useMemo } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  EMPTY_SALE_FORM,
  EMPTY_EXCHANGE,
  EMPTY_FINANCE,
} from "@/lib/constants";
import Drawer, { FormField, FormTextarea } from "@/components/Drawer";
import { SaleVehicleSelect, SaleCustomerSelect } from "@/components/SaleSelects";
import { SalePackagesPicker } from "@/components/PackagesSelector";
import { fmtMoney } from "@/lib/format";
import { computeSaleDue, paymentLinesTotal } from "@/lib/saleMath";
import BranchSelect from "@/components/BranchSelect";
import PaymentLinesEditor from "@/components/PaymentLinesEditor";
import ExchangeBlock from "@/components/ExchangeBlock";
import FinanceBlock from "@/components/FinanceBlock";
import { useAuth } from "@/context/AuthContext";
import CustomerPinGate from "@/components/CustomerPinGate";
import { customerPin } from "@/lib/india";
import { Plus, Minus, ArrowsLeftRight, Bank } from "@phosphor-icons/react";

function AddOnToggles({ hasExchange, hasFinance, onChange }) {
  const Item = ({ active, onClick, icon: Icon, label, hint, testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={`px-3 py-2.5 border text-left transition-colors flex items-center gap-2 ${
        active ? "border-ink bg-ink text-white" : "border-canvas-line text-ink hover:border-ink"
      }`}
    >
      {active ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
      <Icon size={16} weight="bold" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{label}</div>
        <div
          className={`font-mono text-[10px] uppercase tracking-wider ${
            active ? "text-white/70" : "text-ink-subtle"
          }`}
        >
          {hint}
        </div>
      </div>
    </button>
  );
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        Sale composition · Cash is always the base
      </label>
      <div className="grid grid-cols-2 gap-2" data-testid="sale-addons">
        <Item
          active={hasExchange}
          onClick={() => onChange({ has_exchange: !hasExchange })}
          icon={ArrowsLeftRight}
          label={hasExchange ? "Exchange added" : "Add exchange"}
          hint={hasExchange ? "Trade-in vehicle captured" : "Trade-in for credit"}
          testId="addon-exchange"
        />
        <Item
          active={hasFinance}
          onClick={() => onChange({ has_finance: !hasFinance })}
          icon={Bank}
          label={hasFinance ? "Finance added" : "Add finance"}
          hint={hasFinance ? "Financier captured" : "Loan from bank / NBFC"}
          testId="addon-finance"
        />
      </div>
    </div>
  );
}

function GstTypeToggle({ value, onChange }) {
  const opts = [
    { value: "intra", label: "Intra-state", note: "CGST + IGST" },
    { value: "inter", label: "Inter-state", note: "IGST only" },
  ];
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        GST Type
      </label>
      <div className="grid grid-cols-2 gap-2" data-testid="sale-gst-type">
        {opts.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              data-testid={`gst-type-${opt.value}`}
              className={`px-3 py-2 border text-sm text-left transition-colors ${
                active ? "border-ink bg-ink text-white" : "border-canvas-line text-ink hover:border-ink"
              }`}
            >
              <div className="font-semibold">{opt.label}</div>
              <div
                className={`font-mono text-[10px] uppercase tracking-wider ${
                  active ? "text-white/70" : "text-ink-subtle"
                }`}
              >
                {opt.note}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DueStrip({ form, due }) {
  const lineTotal = paymentLinesTotal(form.payment_lines || []);
  const dueClass =
    due > 0 ? "text-destructive" : due < 0 ? "text-amber-500" : "text-[#00B341]";
  const dueLabel =
    due > 0 ? "Balance to collect" : due < 0 ? "Overpaid (refund)" : "Fully settled";
  // Spell out the formula in-place so the salesperson can sanity-check the math.
  const parts = ["Advance", "Discount", "Tenders"];
  if (form.has_exchange) parts.push("Exchange");
  if (form.has_finance) parts.push("Finance", "− Commission");
  const formula = `On-road − (${parts.join(" + ").replace("+ −", "−")})`;
  return (
    <div
      data-testid="sale-due-strip"
      className="border-2 border-ink p-3 bg-canvas-panel"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
            {dueLabel}
          </div>
          <div className={`font-display text-2xl font-bold tabular-nums ${dueClass}`} data-testid="sale-due-value">
            {fmtMoney(Math.abs(due))}
          </div>
        </div>
        <div className="text-right font-mono text-[10px] text-ink-subtle">
          {formula}
          <div className="mt-1">Tenders captured: {fmtMoney(lineTotal)}</div>
          <div className="mt-1 text-ink-muted">
            Will be recorded as PENDING APPROVAL · Due tracked separately
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SaleFormDrawer({ open, vehicles, customers, onClose, onSaved, initialForm }) {
  const [form, setForm] = useState(EMPTY_SALE_FORM);
  const [saving, setSaving] = useState(false);
  const [packageTotal, setPackageTotal] = useState(0);
  const [agents, setAgents] = useState([]);
  const [packagesForVehiclePicker, setPackagesForVehiclePicker] = useState([]);
  const [advanceMeta, setAdvanceMeta] = useState(null);  // {count, total} when prefilled from bookings
  // Admin-only "Override" toggle for the Advance field. Non-admins always see
  // it read-only — the booking is the only source of truth.
  const [advanceUnlocked, setAdvanceUnlocked] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.is_admin || user?.role === "admin";
  const [discountMeta, setDiscountMeta] = useState(null); // {total} when auto-filled from package company_discount
  const [onRoadUserEdited, setOnRoadUserEdited] = useState(false); // sticks once operator types their own on-road price
  // Operator's mandatory disposition when the sale is short-paid (due > 0.01).
  // `mode`: "" (undecided — blocks submit), "due", or "writeoff".
  // `writeoffAmount`: partial amount to waive when mode="writeoff" — if
  // less than the residual, the remainder is auto-left as due (a split).
  const [disposition, setDisposition] = useState({ mode: "", reason: "", writeoffAmount: "" });

  // Discount is locked to the sum of `company_discount` across the
  // selected packages. The field is read-only in the UI (see below), so
  // this effect is the single source of truth: whenever the selection
  // changes, we recompute and stamp it in lock-step. No more "did the
  // user override?" branch — the write-off column handles that case now.
  useEffect(() => {
    if (!open) return;
    const selectedIds = form.package_ids || [];
    if (!selectedIds.length) {
      setForm((f) => (f.discount ? { ...f, discount: 0 } : f));
      setDiscountMeta(null);
      return;
    }
    const pkgsById = new Map((packagesForVehiclePicker || []).map((p) => [p.id, p]));
    let total = 0;
    let resolved = 0;
    for (const id of selectedIds) {
      const p = pkgsById.get(id);
      if (p) {
        total += Number(p.company_discount) || 0;
        resolved += 1;
      }
    }
    // Master hasn't loaded the selected ids yet — wait to avoid clobbering.
    if (resolved === 0) return;
    setForm((f) => (Number(f.discount) === total ? f : { ...f, discount: total }));
    setDiscountMeta(total > 0 ? { total } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.package_ids, packagesForVehiclePicker]);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_SALE_FORM);
      setAdvanceMeta(null);
      setAdvanceUnlocked(false);
      setOnRoadUserEdited(false);
      return;
    }
    // Seed from an explicit prefill (e.g. when converting a booking → sale)
    // so the user lands on the form with everything from the booking already
    // populated and can layer on exchange / finance / extra tenders.
    if (initialForm) {
      setForm({ ...EMPTY_SALE_FORM, ...initialForm });
      // If the prefill carries an on-road price, treat it as user-set so the
      // package-total auto-sync doesn't clobber the converted figure.
      if (Number(initialForm.on_road_price) > 0) {
        setOnRoadUserEdited(true);
      } else {
        setOnRoadUserEdited(false);
      }
      if (initialForm._advance_from_bookings) {
        setAdvanceMeta({
          count: initialForm._advance_from_bookings.count,
          total: initialForm._advance_from_bookings.total,
        });
      }
    } else {
      setOnRoadUserEdited(false);
    }
    // Refresh active agent list whenever the drawer opens (so a newly-added
    // agent shows up without a full page reload). We also pull the package
    // master so the vehicle picker can show each VIN's attached package name —
    // operators recognise "JUPITER 125 BSVI-DISC" much faster than "TVS SCOOTERS".
    // The agents list is re-fetched whenever the branch changes so only the
    // staff actually assigned to that branch appear in the picker.
    let cancelled = false;
    (async () => {
      try {
        const agentParams = { active_only: true };
        if (form.branch_id) agentParams.branch_id = form.branch_id;
        const [agentsRes, pkgsRes] = await Promise.all([
          api.get("/sales-agents", { params: agentParams }),
          api.get("/packages", { params: { active_only: true } }),
        ]);
        if (!cancelled) {
          setAgents(agentsRes.data);
          setPackagesForVehiclePicker(pkgsRes.data || []);
          // If the previously-selected agent isn't valid for the new branch,
          // clear the selection so the operator picks again.
          const stillValid = (agentsRes.data || []).some((a) => a.id === form.sales_agent_id);
          if (form.sales_agent_id && !stillValid) {
            setForm((f) => ({ ...f, sales_agent_id: "" }));
          }
        }
      } catch {
        if (!cancelled) setAgents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, form.branch_id]);

  const selectedVehicle = vehicles.find((v) => v.id === form.vehicle_id);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // The instant the operator types their own On-Road Price, lock it in so
    // subsequent package toggles don't overwrite their figure.
    if (k === "on_road_price") setOnRoadUserEdited(true);
  };

  const handleVehicle = (id) => {
    setForm((f) => ({
      ...f,
      vehicle_id: id,
      package_ids: [],
      package_item_exclusions: {},
    }));
    setPackageTotal(0);
    // Vehicle change implies a fresh sale → let package auto-fill drive
    // on-road price again until the operator types their own value.
    setOnRoadUserEdited(false);
  };

  // When a customer is picked, pull their active bookings and pre-fill the
  // advance field with the sum of payments collected so far. This avoids the
  // salesperson having to re-key the booking advance when converting a walk-in
  // booking into a sale. We do NOT overwrite a value the user has already
  // typed unless we have something better to put there.
  const handleCustomer = async (id) => {
    setForm((f) => ({ ...f, customer_id: id }));
    setAdvanceMeta(null);
    if (!id) return;
    try {
      const { data } = await api.get("/bookings", {
        params: { customer_id: id, status: "active" },
      });
      const bookings = Array.isArray(data) ? data : [];
      const total = bookings.reduce(
        (sum, b) => sum + (Number(b.total_paid) || 0),
        0
      );
      if (bookings.length === 0 || total <= 0) return;
      // Auto-link this sale to the single matching booking so approval will
      // close the booking + free the customer's advance. We only auto-link
      // when there's exactly ONE active booking — if there are multiple,
      // the operator must pick deliberately (via the Convert flow).
      const linkId = bookings.length === 1 ? bookings[0].id : "";
      setForm((f) => ({
        ...f,
        advance_amount: total,
        ...(linkId ? { booking_id: linkId } : {}),
      }));
      setAdvanceMeta({ count: bookings.length, total, booking_id: linkId });
    } catch {
      // Soft-fail: prefill is a nice-to-have, never block the form.
    }
  };

  const handleTotalChange = (sum, selectedPkgs) => {
    setPackageTotal(sum);
    // Keep On-Road Price in sync with the live package total on every toggle,
    // unless the operator has manually typed their own value (tracked via
    // `onRoadUserEdited`). Finance adds a separate hypothecation fee, so we
    // re-add it here whenever finance is currently selected — using
    // `f.has_finance` as the single source of truth avoids racing with the
    // `_hypothecation_applied` bookkeeping flag set by handleAddOnToggle.
    setForm((f) => {
      if (onRoadUserEdited) return f;
      const finBump = f.has_finance ? HYPOTHECATION_FEE : 0;
      // Round to whole rupees — JS floating-point arithmetic on the package
      // item sum produces values like 194888.5999999998 which spook the
      // operator. Indian dealership pricing is always rupee-rounded anyway.
      return { ...f, on_road_price: Math.round(sum + finBump) };
    });
    // Auto-fill the sale's `discount` from each selected package's
    // `company_discount` (dealer-side rebate published at the package master).
    // We only overwrite the existing form.discount if it's still equal to the
    // previous auto-fill — the moment the user types their own value, we stop
    // touching it (tracked via `discountMeta`).
    const totalCompanyDiscount = (selectedPkgs || []).reduce(
      (acc, p) => acc + (Number(p.company_discount) || 0),
      0
    );
    setForm((f) => {
      const currentlyAutofilled = discountMeta && Number(f.discount) === Number(discountMeta.total);
      const isUntouched = !f.discount || Number(f.discount) === 0 || currentlyAutofilled;
      if (isUntouched && totalCompanyDiscount > 0) {
        return { ...f, discount: totalCompanyDiscount };
      }
      return f;
    });
    setDiscountMeta(totalCompanyDiscount > 0 ? { total: totalCompanyDiscount } : null);
  };

  // Toggle exchange/finance independently; instantiate or clear the matching
  // block so the payload only carries what the user actually configured.
  // Finance also auto-adds a ₹500 hypothecation charge to the on-road price
  // (folded in invisibly — no separate line for the customer). We track
  // whether the bump has been applied so re-toggling doesn't double-charge.
  const HYPOTHECATION_FEE = 500;
  const handleAddOnToggle = ({ has_exchange, has_finance }) => {
    setForm((f) => {
      const next = { ...f };
      if (has_exchange !== undefined) {
        next.has_exchange = has_exchange;
        next.exchange = has_exchange ? f.exchange || { ...EMPTY_EXCHANGE } : null;
      }
      if (has_finance !== undefined) {
        next.has_finance = has_finance;
        next.finance = has_finance ? f.finance || { ...EMPTY_FINANCE } : null;
        const wasApplied = !!f._hypothecation_applied;
        const currentOnRoad = Number(f.on_road_price) || 0;
        if (has_finance && !wasApplied) {
          next.on_road_price = currentOnRoad + HYPOTHECATION_FEE;
          next._hypothecation_applied = true;
        } else if (!has_finance && wasApplied) {
          next.on_road_price = Math.max(0, currentOnRoad - HYPOTHECATION_FEE);
          next._hypothecation_applied = false;
        }
      }
      return next;
    });
  };

  const due = useMemo(() => computeSaleDue(form), [form]);
  const [pinFixed, setPinFixed] = useState({});
  const selectedCustomer = customers.find((c) => c.id === form.customer_id);
  const pinMissing = !!selectedCustomer && !customerPin(selectedCustomer) && !pinFixed[form.customer_id];

  const confirm = useConfirm();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pinMissing) {
      toast.error("Customer PIN code is required before creating the sale invoice.");
      return;
    }
    if (!(await confirm({ title: "Submit sale for approval?", body: "This sale will be created and sent for verification. Confirm details before saving." }))) return;
    if (!form.vehicle_id || !form.customer_id) {
      toast.error("Vehicle and customer are required.");
      return;
    }
    if (!form.on_road_price) {
      toast.error("On-road price is required.");
      return;
    }
    if (form.has_exchange && !form.exchange?.evaluator_id) {
      toast.error("Pick the responsible evaluator for the exchange.");
      return;
    }
    if (form.has_finance) {
      if (!form.finance?.financier_id) {
        toast.error("Pick a financier.");
        return;
      }
      if (!form.finance.finance_amount) {
        toast.error("Finance amount is required.");
        return;
      }
    }
    // Residual-due gate — operator must classify a shortfall before submit.
    if (due > 0.01) {
      if (!disposition.mode) {
        toast.error(`Shortfall of ${fmtMoney(due)} — pick "Leave as Due" or "Write-off" first.`);
        return;
      }
      if (disposition.mode === "writeoff") {
        if (!(disposition.reason || "").trim()) {
          toast.error("Write-off requires a short reason (audit).");
          return;
        }
        const wo = Number(disposition.writeoffAmount);
        if (!(wo > 0)) {
          toast.error("Enter the write-off amount (must be greater than 0).");
          return;
        }
        if (wo > due + 0.01) {
          toast.error(`Write-off ${fmtMoney(wo)} can't exceed the residual ${fmtMoney(due)}.`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      // Strip internal-only UI state (e.g. _hypothecation_applied, _advance_from_bookings) before sending.
      const { _hypothecation_applied, _advance_from_bookings, ...formClean } = form;
      const payload = {
        ...formClean,
        on_road_price: Number(form.on_road_price) || 0,
        advance_amount: Number(form.advance_amount) || 0,
        down_payment: Number(form.down_payment) || 0,
        discount: Number(form.discount) || 0,
        sale_price: Number(form.on_road_price) || 0, // legacy alias
      };
      if (due > 0.01) {
        payload.pending_disposition = {
          mode: disposition.mode,
          amount: disposition.mode === "writeoff" ? Number(Number(disposition.writeoffAmount).toFixed(2)) : null,
          reason: disposition.reason || "",
        };
      }
      const { data } = await api.post("/sales", payload);
      toast.success("Sale recorded");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="New Transaction"
      title="Record a sale"
      testIdClose="close-sale-form"
    >
      <form onSubmit={handleSubmit} data-testid="sale-form" className="space-y-4">
        <SaleVehicleSelect
          vehicles={vehicles}
          packages={packagesForVehiclePicker}
          value={form.vehicle_id}
          onChange={handleVehicle}
        />
        {selectedVehicle && (
          <GstTypeToggle value={form.gst_type} onChange={(v) => set("gst_type", v)} />
        )}
        {selectedVehicle && (
          <SalePackagesPicker
            vehicleId={selectedVehicle.id}
            availableIds={selectedVehicle.package_ids || []}
            selected={form.package_ids}
            exclusions={form.package_item_exclusions}
            gstType={form.gst_type}
            onChange={(package_ids, package_item_exclusions) =>
              setForm((f) => ({ ...f, package_ids, package_item_exclusions }))
            }
            onTotalChange={handleTotalChange}
          />
        )}
        <SaleCustomerSelect
          customers={customers}
          value={form.customer_id}
          onChange={handleCustomer}
        />
        {pinMissing && (
          <CustomerPinGate
            key={form.customer_id}
            customerId={form.customer_id}
            onSaved={() => setPinFixed((m) => ({ ...m, [form.customer_id]: true }))}
          />
        )}

        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Sales Agent <span className="text-destructive">*</span>
          </label>
          <select
            required
            value={form.sales_agent_id || ""}
            onChange={(e) => set("sales_agent_id", e.target.value)}
            data-testid="sale-agent"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            <option value="">— Select agent —</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
                {a.code ? ` · ${a.code}` : ""}
              </option>
            ))}
          </select>
          {agents.length === 0 && (
            <p className="font-mono text-[11px] text-destructive mt-1">
              No active sales agents — add one from the Sales Agents page first.
            </p>
          )}
        </div>

        <BranchSelect
          value={form.branch_id}
          onChange={(v) => set("branch_id", v)}
          testId="sale-branch"
          required
        />

        <AddOnToggles
          hasExchange={form.has_exchange}
          hasFinance={form.has_finance}
          onChange={handleAddOnToggle}
        />

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="On-Road Price (₹)"
            type="number"
            value={form.on_road_price}
            onChange={(v) => set("on_road_price", v)}
            testId="sale-on-road"
            required
          />
          <div>
            {/* Discount is sourced ONLY from the selected package(s)
                `company_discount`. Operators cannot override it because
                any additional waiver at closing time now flows through the
                mandatory Write-Off disposition (surfaces on the Write-Off
                & Excess report). Keeping it read-only stops branch staff
                from silently inflating dealer-cost margins. */}
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Discount (₹) · from package
            </label>
            <input
              type="number"
              value={form.discount || 0}
              readOnly
              disabled
              data-testid="sale-discount"
              className="w-full px-3 py-2 bg-canvas-panel border border-canvas-line text-sm cursor-not-allowed opacity-70"
            />
            {discountMeta ? (
              <p
                data-testid="sale-discount-prefill-note"
                className="font-mono text-[10px] text-[#00B341] mt-1"
              >
                Auto-filled from package company discount ({fmtMoney(discountMeta.total)}) · locked
              </p>
            ) : (
              <p className="font-mono text-[10px] text-ink-muted mt-1">
                Any additional waiver goes into Write-Off at the payment mix section.
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            {/* Advance is sourced ONLY from the customer's open booking — it
                must never be hand-edited in the normal flow (a misclick here
                inflates the credits side and creates phantom overpayments).
                The field is rendered read-only with the booking amount; admin
                users can still override via the "Edit" toggle for rare cases
                (e.g. correcting an old migration). */}
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Advance Amount (₹) · from booking
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                readOnly={!advanceUnlocked}
                value={form.advance_amount || 0}
                onChange={(e) => {
                  if (!advanceUnlocked) return;
                  set("advance_amount", e.target.value);
                  if (advanceMeta) setAdvanceMeta(null);
                }}
                data-testid="sale-advance"
                placeholder="0"
                className={`w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink ${
                  advanceUnlocked ? "" : "bg-canvas-panel cursor-not-allowed"
                }`}
              />
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setAdvanceUnlocked((v) => !v)}
                  data-testid="sale-advance-unlock"
                  title={
                    advanceUnlocked
                      ? "Re-lock — go back to booking-only source"
                      : "Admin override — edit advance manually (use sparingly)"
                  }
                  className="px-2 py-2 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink whitespace-nowrap"
                >
                  {advanceUnlocked ? "Lock" : "Override"}
                </button>
              )}
            </div>
            {advanceMeta && (
              <p
                data-testid="sale-advance-prefill-note"
                className="font-mono text-[10px] text-[#00B341] mt-1"
              >
                Pre-filled from {advanceMeta.count} active booking
                {advanceMeta.count > 1 ? "s" : ""} ({fmtMoney(advanceMeta.total)})
                {advanceMeta.booking_id ? " · booking will close on approval" : " · multiple bookings — convert manually"}
              </p>
            )}
          </div>
          <div />
        </div>

        {form.has_exchange && (
          <ExchangeBlock
            value={form.exchange}
            onChange={(ex) => set("exchange", ex)}
          />
        )}
        {form.has_finance && (
          <FinanceBlock
            value={form.finance}
            onChange={(fin) => set("finance", fin)}
          />
        )}

        {advanceMeta && (
          <div
            data-testid="sale-advance-double-count-warning"
            className="px-3 py-2 border border-[#B58900] bg-[#FFFBE6] text-[11px] font-mono text-[#7A5C00]"
          >
            <b>HEADS UP:</b> {fmtMoney(advanceMeta.total)} already credited via
            booking advance (shown above). Do NOT re-add it here — only enter
            <b> new payments</b> collected at delivery. Adding the same amount
            in both places will inflate the receipts and trigger an
            <b> OVERPAID</b> warning at submit.
          </div>
        )}

        <PaymentLinesEditor
          value={form.payment_lines}
          onChange={(lines) => set("payment_lines", lines)}
        />

        <DueStrip form={form} due={due} />

        {/* Mandatory disposition picker — only shown when there's a real
            shortfall. Excess (over-paid) is auto-categorised server-side. */}
        {due > 0.01 && (
          <div className="border-2 border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="pending-disposition-block">
            <div className="font-mono text-[11px] uppercase tracking-widest text-amber-800">
              Shortfall of {fmtMoney(due)} — pick one <span className="text-destructive">*</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className={`flex items-start gap-2 p-2.5 border cursor-pointer ${disposition.mode === "due" ? "border-ink bg-white" : "border-canvas-line bg-white/60 hover:border-ink"}`}>
                <input
                  type="radio"
                  name="disposition-mode"
                  value="due"
                  checked={disposition.mode === "due"}
                  onChange={() => setDisposition({ ...disposition, mode: "due" })}
                  data-testid="disposition-mode-due"
                  className="mt-0.5"
                />
                <div className="text-xs">
                  <div className="font-semibold">Leave as Due</div>
                  <div className="text-[11px] text-ink-muted">Customer will pay {fmtMoney(due)} later. Shows on the Retail Dues report.</div>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-2.5 border cursor-pointer ${disposition.mode === "writeoff" ? "border-destructive bg-white" : "border-canvas-line bg-white/60 hover:border-destructive"}`}>
                <input
                  type="radio"
                  name="disposition-mode"
                  value="writeoff"
                  checked={disposition.mode === "writeoff"}
                  onChange={() => setDisposition({ ...disposition, mode: "writeoff", writeoffAmount: disposition.writeoffAmount || due.toFixed(2) })}
                  data-testid="disposition-mode-writeoff"
                  className="mt-0.5"
                />
                <div className="text-xs flex-1">
                  <div className="font-semibold">Write-off (partial or full)</div>
                  <div className="text-[11px] text-ink-muted">Waive part of the shortfall as loss. Any remainder is left as Due.</div>
                </div>
              </label>
            </div>
            {disposition.mode === "writeoff" && (() => {
              const wo = Number(disposition.writeoffAmount) || 0;
              const remainder = Math.max(0, Number((due - wo).toFixed(2)));
              const invalid = wo > due + 0.01;
              return (
                <div className="flex items-center gap-2 pl-2">
                  <label className="text-[11px] font-mono uppercase tracking-wider text-amber-800 whitespace-nowrap">Write-off ₹</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    max={due.toFixed(2)}
                    value={disposition.writeoffAmount}
                    onChange={(e) => setDisposition({ ...disposition, writeoffAmount: e.target.value })}
                    data-testid="disposition-writeoff-amount"
                    className={`w-32 px-2 py-1 border bg-white text-sm font-mono tabular-nums ${invalid ? "border-destructive text-destructive" : "border-canvas-line"}`}
                  />
                  <button
                    type="button"
                    onClick={() => setDisposition({ ...disposition, writeoffAmount: due.toFixed(2) })}
                    data-testid="disposition-writeoff-full"
                    className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider text-ink-muted hover:border-ink hover:text-ink"
                    title="Write off the full shortfall"
                  >
                    Full
                  </button>
                  <div className="flex-1 text-[11px] font-mono text-ink-muted" data-testid="disposition-split-hint">
                    {invalid
                      ? <span className="text-destructive">Exceeds shortfall by {fmtMoney(wo - due)}</span>
                      : remainder > 0.01
                        ? <>Remaining <b>{fmtMoney(remainder)}</b> will be left as <b>Due</b> (split)</>
                        : "Full shortfall waived — no remainder"}
                  </div>
                </div>
              );
            })()}
            <input
              type="text"
              placeholder={disposition.mode === "writeoff" ? "Reason (required) — e.g. rounding, card surcharge waived" : "Optional note"}
              value={disposition.reason}
              onChange={(e) => setDisposition({ ...disposition, reason: e.target.value })}
              data-testid="disposition-reason"
              className="w-full px-3 py-2 border border-canvas-line bg-white text-xs font-mono"
            />
          </div>
        )}

        {packageTotal > 0 && (
          <div className="px-3 py-2 border border-canvas-line bg-canvas-panel font-mono text-[11px] text-ink">
            <div className="flex justify-between text-ink-muted">
              <span>Packages (ticked items)</span>
              <span>{fmtMoney(packageTotal)}</span>
            </div>
          </div>
        )}

        <FormTextarea
          label="Notes"
          value={form.notes}
          onChange={(v) => set("notes", v)}
          testId="sale-notes"
        />

        {/* Sticky action row — kept ~24px above the viewport bottom so the
            Emergent platform badge (fixed bottom-right) never overlaps the
            Submit button. Trailing spacer below pushes the form's last
            section above this sticky row. */}
        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6 sticky bottom-6 bg-canvas-card pb-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="confirm-sale-button"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60"
          >
            {saving ? "Recording\u2026" : "Submit for approval"}
          </button>
        </div>
        <div aria-hidden className="h-16" />
      </form>
    </Drawer>
  );
}
